<script setup lang="ts">
import {computed} from "vue";
import {PopoverClose} from "reka-ui";
import {BellRing, Cake} from "@lucide/vue";
import {Popover, PopoverContent, PopoverTrigger} from "@/core/components/ui/popover";
import {AgendaAppointment} from "@/core/interfaces/AgendaAppointment";
import {AgendaRenewalReminder} from "@/core/interfaces/AgendaRenewalReminder";
import {AgendaBirthday} from "@/core/interfaces/AgendaBirthday";
import {birthdayNames} from "@/agenda/lib/birthdays";
import {formatAgendaTime, parseAgendaDate} from "@/agenda/lib/agendaTime";
import {monthWeeks} from "@/agenda/lib/agendaViews";
import {DEFAULT_APPOINTMENT_COLOR, MONTH_CELL_APPOINTMENTS} from "@/agenda/constants";
import {appointmentLabel, appointmentPatient} from "@/agenda/lib/appointmentLabel";
import AgendaReminderChip from "@/agenda/components/AgendaReminderChip.vue";

const props = defineProps<{
    date: Date;
    appointments: AgendaAppointment[];
    reminders?: AgendaRenewalReminder[];
    /** Targeted patient birthdays of the month, shown as a count per cell. */
    birthdays?: AgendaBirthday[];
}>();

const emits = defineEmits<{
    (e: 'appointment-click', appointment: AgendaAppointment): void;
    (e: 'reminder-click', reminder: AgendaRenewalReminder): void;
    (e: 'day-click', day: Date): void;
    (e: 'birthdays-click', day: Date): void;
}>();

const weekDayLabels = computed(() => {
    return monthWeeks(props.date)[0].map((day) => day.format('ddd'));
});

/**
 * Appointments bucketed by calendar day, so each cell only walks its own.
 * A night appointment spans two days and is counted on both, otherwise the
 * second day would show neither a counter nor the appointment while the day
 * view does display it.
 */
const appointmentsByDay = computed(() => {
    const buckets: Record<string, AgendaAppointment[]> = {};

    props.appointments.forEach((appointment) => {
        const end = parseAgendaDate(appointment.end_at);
        let cursor = parseAgendaDate(appointment.start_at).startOf('day');

        while (cursor.isBefore(end)) {
            (buckets[cursor.format('YYYY-MM-DD')] ??= []).push(appointment);
            cursor = cursor.add(1, 'day');
        }
    });

    return buckets;
});

/**
 * Reminders bucketed by the day they are sent on. Unlike an appointment a
 * reminder is a single moment, so it belongs to exactly one cell.
 */
const remindersByDay = computed(() => {
    const buckets: Record<string, AgendaRenewalReminder[]> = {};

    (props.reminders ?? []).forEach((reminder) => {
        const day = parseAgendaDate(reminder.reminder_at).format('YYYY-MM-DD');

        (buckets[day] ??= []).push(reminder);
    });

    return buckets;
});

/**
 * Birthdays bucketed by the day they are celebrated on, already a date key
 * server-side.
 */
const birthdaysByDay = computed(() => {
    const buckets: Record<string, AgendaBirthday[]> = {};

    (props.birthdays ?? []).forEach((birthday) => {
        (buckets[birthday.celebrated_on] ??= []).push(birthday);
    });

    return buckets;
});

const weeks = computed(() => {
    const currentMonth = parseAgendaDate(props.date).month();
    const today = parseAgendaDate(new Date());

    return monthWeeks(props.date).map((week) => week.map((day) => {
        const appointments = appointmentsByDay.value[day.format('YYYY-MM-DD')] ?? [];
        const reminders = remindersByDay.value[day.format('YYYY-MM-DD')] ?? [];
        const birthdays = birthdaysByDay.value[day.format('YYYY-MM-DD')] ?? [];

        return {
            day,
            key: day.format('YYYY-MM-DD'),
            label: day.date(),
            outside: day.month() !== currentMonth,
            isToday: day.isSame(today, 'day'),
            appointments,
            reminders,
            birthdays,
            // Listed in the cell, the rest summarized by a "+N" opening the
            // whole day.
            visible: appointments.slice(0, MONTH_CELL_APPOINTMENTS),
            hidden: Math.max(0, appointments.length - MONTH_CELL_APPOINTMENTS),
            busy: appointments.length > 0 || reminders.length > 0 || birthdays.length > 0
        };
    }));
});

const labelOf = (appointment: AgendaAppointment) => appointmentLabel(appointment);

const patientOf = (appointment: AgendaAppointment) => appointmentPatient(appointment);

const colorOf = (appointment: AgendaAppointment) => {
    return appointment.appointment_type?.color ?? DEFAULT_APPOINTMENT_COLOR;
};

/**
 * Hover text of a cell entry: the cell is too narrow to show the patient
 * beside the label.
 */
const titleOf = (appointment: AgendaAppointment) => {
    const patient = patientOf(appointment);

    return `${formatAgendaTime(appointment.start_at)} ${labelOf(appointment)}${patient ? ` — ${patient}` : ''}`;
};
</script>

<template>
  <div class="flex h-full flex-col">
    <!--
      The weekday row lives inside the scroll container, sticky: outside of it
      it would keep the scrollbar's width while the day columns lose it, and a
      month tall enough to scroll would show every label offset from the column
      it names (platforms whose scrollbars take layout space).
    -->
    <div class="flex flex-1 flex-col overflow-y-auto">
      <div class="sticky top-0 z-10 flex shrink-0 border-b border-gray-200 bg-white">
        <div
          v-for="label in weekDayLabels"
          :key="label"
          class="min-w-0 flex-1 py-2 text-center text-sm font-medium capitalize"
        >
          {{ label }}
        </div>
      </div>
      <div v-for="(week, index) in weeks" :key="index" class="flex flex-1 border-b border-gray-100">
        <div
          v-for="cell in week"
          :key="cell.key"
          class="flex min-h-24 min-w-0 flex-1 flex-col gap-1 border-l border-gray-100 p-1.5"
          :class="cell.outside ? 'bg-gray-50/60' : ''"
        >
          <div class="flex items-center justify-between gap-1">
            <button
              type="button"
              class="flex size-6 items-center justify-center rounded-full text-xs tabular-nums transition-colors hover:bg-gray-100"
              :class="[
                cell.isToday ? 'bg-primary font-semibold text-white hover:bg-primary' : '',
                cell.outside ? 'text-gray-400' : '',
                !cell.isToday && cell.busy ? 'font-semibold' : ''
              ]"
              :aria-label="`Voir le ${cell.day.format('D MMMM')}`"
              @click="emits('day-click', cell.day.toDate())"
            >
              {{ cell.label }}
            </button>
            <button
              v-if="cell.birthdays.length"
              type="button"
              class="flex items-center gap-0.5 rounded-full bg-pink/20 px-1.5 py-0.5 text-xs font-medium text-gray-dark transition-colors hover:bg-pink/30"
              :title="birthdayNames(cell.birthdays)"
              :aria-label="`${cell.birthdays.length} anniversaire(s) le ${cell.day.format('D MMMM')}`"
              @click="emits('birthdays-click', cell.day.toDate())"
            >
              <Cake class="size-3" />
              {{ cell.birthdays.length }}
            </button>
            <Popover v-if="cell.reminders.length">
              <PopoverTrigger as-child>
                <button
                  type="button"
                  class="flex items-center gap-0.5 rounded-full bg-orange-light/50 px-1.5 py-0.5 text-xs font-medium transition-colors hover:bg-orange-light"
                  :aria-label="`${cell.reminders.length} rappel(s) de renouvellement le ${cell.day.format('D MMMM')}`"
                >
                  <BellRing class="size-3" />
                  {{ cell.reminders.length }}
                </button>
              </PopoverTrigger>
              <PopoverContent class="w-80 p-0" side="bottom" align="end">
                <p class="border-b border-gray-100 px-3 py-2 text-sm font-medium capitalize">
                  Rappels du {{ cell.day.format('dddd D MMMM') }}
                </p>
                <div class="flex max-h-64 flex-col gap-1 overflow-y-auto p-1.5">
                  <PopoverClose
                    v-for="reminder in cell.reminders"
                    :key="reminder.id"
                    as-child
                  >
                    <AgendaReminderChip
                      :reminder="reminder"
                      @click="emits('reminder-click', reminder)"
                    />
                  </PopoverClose>
                </div>
              </PopoverContent>
            </Popover>
          </div>
          <!--
            The appointments themselves, not just their count: a month cell is
            96px tall and a lone counter pill made a busy day look empty. Each
            entry wears its type colour, like the cards of the other views.
          -->
          <button
            v-for="appointment in cell.visible"
            :key="appointment.id"
            type="button"
            class="flex min-w-0 items-center gap-1 overflow-hidden rounded border-l-2 px-1 py-0.5 text-left text-[11px] leading-tight transition-shadow hover:shadow-sm"
            :style="{borderLeftColor: colorOf(appointment), backgroundColor: `${colorOf(appointment)}1A`}"
            :title="titleOf(appointment)"
            @click="emits('appointment-click', appointment)"
          >
            <span class="shrink-0 tabular-nums text-gray-600">
              {{ formatAgendaTime(appointment.start_at) }}
            </span>
            <span class="truncate font-medium">{{ labelOf(appointment) }}</span>
          </button>
          <Popover v-if="cell.hidden">
            <PopoverTrigger as-child>
              <button
                type="button"
                class="rounded px-1 text-left text-[11px] font-medium text-primary transition-colors hover:bg-primary-lightest"
                :aria-label="`Voir les ${cell.appointments.length} rendez-vous du ${cell.day.format('D MMMM')}`"
              >
                + {{ cell.hidden }} autre{{ cell.hidden > 1 ? 's' : '' }}
              </button>
            </PopoverTrigger>
            <PopoverContent class="w-72 p-0" side="bottom" align="end">
              <p class="border-b border-gray-100 px-3 py-2 text-sm font-medium capitalize">
                {{ cell.day.format('dddd D MMMM') }}
              </p>
              <div class="max-h-64 overflow-y-auto py-1">
                <PopoverClose
                  v-for="appointment in cell.appointments"
                  :key="appointment.id"
                  class="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors hover:bg-gray-50"
                  @click="emits('appointment-click', appointment)"
                >
                  <span
                    class="size-2 shrink-0 rounded-full"
                    :style="{backgroundColor: colorOf(appointment)}"
                  />
                  <span class="shrink-0 tabular-nums text-gray-500">
                    {{ formatAgendaTime(appointment.start_at) }}
                  </span>
                  <span class="truncate">{{ labelOf(appointment) }}</span>
                  <span v-if="patientOf(appointment)" class="truncate text-gray-500">
                    {{ patientOf(appointment) }}
                  </span>
                </PopoverClose>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  </div>
</template>
